import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { schoolsApi, lectureLengthsApi } from "../api/client";
import type { School, LectureLength } from "../types";
import "./SchoolManager.css";

interface SchoolManagerProps {
  onSchoolsChange: () => void;
  canEdit?: boolean;
}

export const SchoolManager: React.FC<SchoolManagerProps> = ({
  onSchoolsChange,
  canEdit = true,
}) => {
  const { t } = useTranslation();
  const [schools, setSchools] = useState<School[]>([]);
  const [lectureLengths, setLectureLengths] = useState<LectureLength[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [showLectureLengthModal, setShowLectureLengthModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState<School | null>(null);
  const [editingLectureLength, setEditingLectureLength] =
    useState<LectureLength | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSchools();
  }, []);

  useEffect(() => {
    if (selectedSchool) {
      loadLectureLengths(selectedSchool.id);
    }
  }, [selectedSchool]);

  const loadSchools = async () => {
    try {
      const response = await schoolsApi.getAll();
      setSchools(response.data);
      if (response.data.length > 0 && !selectedSchool) {
        setSelectedSchool(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to load schools:", error);
    }
  };

  const loadLectureLengths = async (schoolId: number) => {
    try {
      const response = await lectureLengthsApi.getBySchool(schoolId);
      setLectureLengths(response.data);
    } catch (error) {
      console.error("Failed to load lecture lengths:", error);
    }
  };

  const handleCreateSchool = async (schoolData: {
    name: string;
    description?: string;
  }) => {
    setLoading(true);
    try {
      await schoolsApi.create(schoolData);
      await loadSchools();
      onSchoolsChange();
    } catch (error) {
      console.error("Failed to create school:", error);
    } finally {
      setLoading(false);
      setShowSchoolModal(false);
    }
  };

  const handleUpdateSchool = async (
    id: number,
    schoolData: { name: string; description?: string },
  ) => {
    setLoading(true);
    try {
      await schoolsApi.update(id, schoolData);
      await loadSchools();
      onSchoolsChange();
    } catch (error) {
      console.error("Failed to update school:", error);
    } finally {
      setLoading(false);
      setShowSchoolModal(false);
      setEditingSchool(null);
    }
  };

  const handleDeleteSchool = async (id: number) => {
    if (!confirm(t("schools.confirmDelete"))) return;

    setLoading(true);
    try {
      await schoolsApi.delete(id);
      await loadSchools();
      onSchoolsChange();
      if (selectedSchool?.id === id) {
        setSelectedSchool(null);
      }
    } catch (error) {
      console.error("Failed to delete school:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLectureLength = async (lectureLengthData: {
    duration: number;
    name: string;
    description?: string;
  }) => {
    if (!selectedSchool) return;

    setLoading(true);
    try {
      await lectureLengthsApi.create({
        ...lectureLengthData,
        school_id: selectedSchool.id,
      });
      await loadLectureLengths(selectedSchool.id);
    } catch (error) {
      console.error("Failed to create lecture length:", error);
    } finally {
      setLoading(false);
      setShowLectureLengthModal(false);
    }
  };

  const handleUpdateLectureLength = async (
    id: number,
    lectureLengthData: { duration: number; name: string; description?: string },
  ) => {
    if (!selectedSchool) return;

    setLoading(true);
    try {
      await lectureLengthsApi.update(id, {
        ...lectureLengthData,
        school_id: selectedSchool.id,
      });
      await loadLectureLengths(selectedSchool.id);
    } catch (error) {
      console.error("Failed to update lecture length:", error);
    } finally {
      setLoading(false);
      setShowLectureLengthModal(false);
      setEditingLectureLength(null);
    }
  };

  const handleDeleteLectureLength = async (id: number) => {
    if (!confirm(t("lectureLengths.confirmDelete"))) return;

    setLoading(true);
    try {
      await lectureLengthsApi.delete(id);
      if (selectedSchool) {
        await loadLectureLengths(selectedSchool.id);
      }
    } catch (error) {
      console.error("Failed to delete lecture length:", error);
    } finally {
      setLoading(false);
    }
  };

  const openSchoolModal = (school?: School) => {
    setEditingSchool(school || null);
    setShowSchoolModal(true);
  };

  const openLectureLengthModal = (lectureLength?: LectureLength) => {
    setEditingLectureLength(lectureLength || null);
    setShowLectureLengthModal(true);
  };

  return (
    <div className="school-manager">
      <div className="school-manager__header">
        <h2>{t("schools.title")}</h2>
        <button
          className="btn btn--primary"
          onClick={() => openSchoolModal()}
          disabled={!canEdit}
        >
          + {t("schools.addSchool")}
        </button>
      </div>

      <div className="school-manager__content">
        <div className="school-manager__schools">
          <h3>{t("schools.schoolsList")}</h3>
          {schools.length === 0 ? (
            <div className="empty-state">
              <p>{t("schools.noSchools")}</p>
            </div>
          ) : (
            <div className="schools-grid">
              {schools.map((school) => (
                <div
                  key={school.id}
                  className={`school-card ${selectedSchool?.id === school.id ? "school-card--selected" : ""}`}
                  onClick={() => setSelectedSchool(school)}
                >
                  <div className="school-card__content">
                    <h4>{school.name}</h4>
                    {school.description && <p>{school.description}</p>}
                  </div>
                  <div className="school-card__actions">
                    <button
                      className="btn btn--icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSchoolModal(school);
                      }}
                      title={t("common.edit")}
                      disabled={!canEdit}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn btn--icon btn--danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSchool(school.id);
                      }}
                      title={t("common.delete")}
                      disabled={!canEdit}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedSchool && (
          <div className="school-manager__lecture-lengths">
            <div className="lecture-lengths__header">
              <h3>
                {t("lectureLengths.title")} - {selectedSchool.name}
              </h3>
              <button
                className="btn btn--primary"
                onClick={() => openLectureLengthModal()}
                disabled={!canEdit}
              >
                + {t("lectureLengths.addLectureLength")}
              </button>
            </div>

            {lectureLengths.length === 0 ? (
              <div className="empty-state">
                <p>{t("lectureLengths.noLectureLengths")}</p>
              </div>
            ) : (
              <div className="lecture-lengths-grid">
                {lectureLengths.map((lectureLength) => (
                  <div key={lectureLength.id} className="lecture-length-card">
                    <div className="lecture-length-card__content">
                      <h4>{lectureLength.name}</h4>
                      <p className="lecture-length-card__duration">
                        {lectureLength.duration} {t("lectureLengths.minutes")}
                      </p>
                      {lectureLength.description && (
                        <p className="lecture-length-card__description">
                          {lectureLength.description}
                        </p>
                      )}
                    </div>
                    <div className="lecture-length-card__actions">
                      <button
                        className="btn btn--icon"
                        onClick={() => openLectureLengthModal(lectureLength)}
                        title={t("common.edit")}
                        disabled={!canEdit}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn--icon btn--danger"
                        onClick={() =>
                          handleDeleteLectureLength(lectureLength.id)
                        }
                        title={t("common.delete")}
                        disabled={!canEdit}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* School Modal */}
      {showSchoolModal && (
        <SchoolModal
          school={editingSchool}
          onSave={editingSchool ? handleUpdateSchool : handleCreateSchool}
          onClose={() => {
            setShowSchoolModal(false);
            setEditingSchool(null);
          }}
          loading={loading}
        />
      )}

      {/* Lecture Length Modal */}
      {showLectureLengthModal && selectedSchool && (
        <LectureLengthModal
          lectureLength={editingLectureLength}
          onSave={
            editingLectureLength
              ? handleUpdateLectureLength
              : handleCreateLectureLength
          }
          onClose={() => {
            setShowLectureLengthModal(false);
            setEditingLectureLength(null);
          }}
          loading={loading}
        />
      )}
    </div>
  );
};

// School Modal Component
interface SchoolModalProps {
  school?: School | null;
  onSave: (
    id: number,
    data: { name: string; description?: string },
  ) => void | ((data: { name: string; description?: string }) => void);
  onClose: () => void;
  loading: boolean;
}

const SchoolModal: React.FC<SchoolModalProps> = ({
  school,
  onSave,
  onClose,
  loading,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: school?.name || "",
    description: school?.description || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (school) {
      (
        onSave as (
          id: number,
          data: { name: string; description?: string },
        ) => void
      )(school.id, formData);
    } else {
      (onSave as (data: { name: string; description?: string }) => void)(
        formData,
      );
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3>{school ? t("schools.editSchool") : t("schools.addSchool")}</h3>
          <button className="modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal__form">
          <div className="form-group">
            <label htmlFor="name">{t("schools.name")}</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">{t("schools.description")}</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </div>
          <div className="modal__actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn--secondary"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading}
            >
              {loading ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Lecture Length Modal Component
interface LectureLengthModalProps {
  lectureLength?: LectureLength | null;
  onSave: (
    id: number,
    data: { duration: number; name: string; description?: string },
  ) =>
    | void
    | ((data: {
        duration: number;
        name: string;
        description?: string;
      }) => void);
  onClose: () => void;
  loading: boolean;
}

const LectureLengthModal: React.FC<LectureLengthModalProps> = ({
  lectureLength,
  onSave,
  onClose,
  loading,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    duration: lectureLength?.duration || 45,
    name: lectureLength?.name || "",
    description: lectureLength?.description || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (lectureLength) {
      (
        onSave as (
          id: number,
          data: { duration: number; name: string; description?: string },
        ) => void
      )(lectureLength.id, formData);
    } else {
      (
        onSave as (data: {
          duration: number;
          name: string;
          description?: string;
        }) => void
      )(formData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3>
            {lectureLength
              ? t("lectureLengths.editLectureLength")
              : t("lectureLengths.addLectureLength")}
          </h3>
          <button className="modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal__form">
          <div className="form-group">
            <label htmlFor="name">{t("lectureLengths.name")}</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="duration">{t("lectureLengths.duration")}</label>
            <input
              id="duration"
              type="number"
              min="1"
              step="1"
              value={formData.duration}
              onChange={(e) =>
                setFormData({ ...formData, duration: parseInt(e.target.value) })
              }
              required
            />
            <small>{t("lectureLengths.durationHelp")}</small>
          </div>
          <div className="form-group">
            <label htmlFor="description">
              {t("lectureLengths.description")}
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </div>
          <div className="modal__actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn--secondary"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading}
            >
              {loading ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
